import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Paginator } from 'src/app/shared/models/paginator';
import { TableHeader } from 'src/app/shared/models/table';
import { PageableService } from 'src/app/shared/services/pageable.service';
import { environment } from 'src/environments/environment';
import { GrupoReporte } from '../../data-visualization/reports/models/reporte';
import { User } from '../../user/user';
import { ReportGroup } from './report-group';
@Injectable({
  providedIn: 'root'
})
export class ReportGroupService {
  public reportGroupsUrl = `${environment.urlBase}/grupo-reporte/`;
  public reportGroupUsersUrl = `${environment.urlBase}/usuario-grupo-reporte/`;

  constructor(private http: HttpClient, private pageableService: PageableService) { }
  /**
   * This function gets the list of report groups by page
   * It can get some params in order to filter or sort the data
   * @param {number} page
   * @param {number} pageQuantity
   * @param {string} header
   * @param {string} filter
   * @param {string} order
   * @returns {Observable<Paginator<ReportGroup>>}
   */
  getReportGroups(page: number, pageQuantity: number, header?: string, filter?: string, order?: string): Observable<Paginator<ReportGroup>> {
    return this.pageableService.getData<ReportGroup>(this.reportGroupsUrl, page, pageQuantity, header, filter, order);
  }

  /**
   * Gets all the list of report groups sending filters
   * @param {BehaviorSubject} search$
   * @param {number} page
   * @returns {Observable<Paginator<ReportGroup>>}
   */
  getReportGroupListByFilters(search$: BehaviorSubject<TableHeader | undefined>, page: number, pageQuantity: number): Observable<Paginator<ReportGroup>> {
    return this.pageableService.searchData<ReportGroup>(search$, page, pageQuantity, this.reportGroupsUrl);
  }

  /**
   * This function creates a report group
   * @param {ReportGroup} reportGroup
   * @returns {Observable<ReportGroup>}
   */
  createReportGroup(reportGroup: ReportGroup): Observable<ReportGroup> {
    return this.http.post<ReportGroup>(this.reportGroupsUrl, reportGroup);
  }

  /**
   * This function updates a report group
   * @param {ReportGroup} reportGroup
   * @returns {Observable<ReportGroup>}
   */
  updateReportGroup(reportGroup: ReportGroup): Observable<ReportGroup> {
    return this.http.put<ReportGroup>(this.reportGroupsUrl, reportGroup);
  }

  /**
   * Get all the report group users by page
   * It can get some params in order to filter or sort the data
   * @param {number} page
   * @param {number} pageQuantity
   * @param {string} header
   * @param {string} filter
   * @param {string} order
   * @returns {Observable<Paginator<User>>}
   */
  getUsers(page: number, pageQuantity: number, header?: string, filter?: string, order?: string): Observable<Paginator<User>> {
    return this.pageableService.getData<User>(this.reportGroupUsersUrl, page, pageQuantity, header, filter, order);
  }

  /**
   * Gets the list of users associated with a report group, sending filters
   * @param {BehaviorSubject} search$
   * @param {number} page
   * @param {number} pageQuantity
   * @param {string} reportGroupId
   * @returns {Observable<Paginator<User>>}
   */
  getUsersByFilters(search$: BehaviorSubject<TableHeader | undefined>, page: number, pageQuantity: number, reportGroupId: string): Observable<Paginator<User>> {
    return this.pageableService.searchData<User>(search$, page, pageQuantity, `${this.reportGroupUsersUrl}${reportGroupId}`);
  }

  /**
   * Returns the endpoint to get users not associated to a report group
   * @param {string} reportGroupId
   * @returns {string}
   */
  usersNotAssociatedUrl(reportGroupId: string): string {
    return `${this.reportGroupUsersUrl}no-asociados/${reportGroupId}`;
  }

  /**
   * A function to associate the user to a report group
   * @param {reportGroupId} reportGroupId
   * @param {string} userUuid
   * @returns {Observable<{}>}
   */
  addUserToReportGroup(reportGroupId: string, userUuid: string): Observable<object> {
    return this.http.put(`${this.reportGroupUsersUrl}${reportGroupId}/agregar/${userUuid}`, null);
  }

  /**
   * This function removes the user from the current report group
   * @param {string} reportGroupId
   * @param {string} userUuid
   * @returns {Observable<{}>}
   */
  deleteUserFromReportGroup(reportGroupId: string, userUuid: string): Observable<object> {
    return this.http.delete(`${this.reportGroupUsersUrl}${reportGroupId}/eliminar/${userUuid}`);
  }

  /**
   * Gets selected report group
   * @param {string} reportGroupId
   * @returns {Observable<GrupoReporte>}
   */
  getReportGroup(reportGroupId: string): Observable<GrupoReporte> {
    return this.http.get<GrupoReporte>(`${this.reportGroupsUrl}${reportGroupId}`);
  }

  /**
   * Add all report groups to the current user
   * @param {string} userUuid
   * @returns {Observable<ReportGroup[]>}
   */
  addAllReportGroupsToUser(userUuid: string): Observable<ReportGroup[]> {
    return this.http.put<ReportGroup[]>(`${this.reportGroupsUrl}asignar-todo/usuario/${userUuid}`, null);
  }

}